/**
 * The language layer.
 *
 * Three rules hold it together:
 *
 * 1. **The code is English.** Keys are dotted and namespaced — `customers.merge.title` — never the
 *    English sentence itself. An English-as-key scheme means every reworded sentence is a rename
 *    across the codebase, and the day somebody fixes a typo in the source language every other
 *    translation silently falls back to it.
 *
 * 2. **The base catalogues are code.** They ship in the bundle, are reviewed with the change that
 *    needed them, and are the same for every workspace.
 *
 * 3. **A workspace can reword any of it**, and those overrides are data. They are fetched at boot
 *    and merged on top — one shop calls a customer a *rytter*, and nobody forks the application.
 *
 * The server takes part in this: validation messages come back as keys, so they are translated and
 * overridable like everything else. Anything that is not a known key falls through unchanged, which
 * is what makes the migration incremental rather than a single flag day.
 */
import { createI18n } from 'vue-i18n'
import da from './locales/da.json'
import en from './locales/en.json'

export const SUPPORTED_LOCALES = ['da', 'en'] as const
export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number]

/** Danish, because the first workspaces are Danish shops. */
export const DEFAULT_LOCALE: SupportedLocale = "da"

/** Each language named in itself, which is how a person recognises their own. */
export const LOCALE_NAMES: Record<SupportedLocale, string> = {
  da: "Dansk",
  en: "English",
}

export function isSupported(locale: string | null | undefined): locale is SupportedLocale {
  return !!locale && (SUPPORTED_LOCALES as readonly string[]).includes(locale)
}

/**
 * The catalogues as they shipped, taken before anything can touch them.
 *
 * `createI18n` keeps the objects it is handed rather than copying them, and `mergeLocaleMessage`
 * merges a workspace's overrides straight into them. So the imported JSON *is* the running
 * catalogue: reading `da` after boot gives whatever the workspace has reworded, not what the
 * release said. Everything downstream of that looked right and was wrong — the override screen
 * showed a phrase as its own default, then reported that the default had changed since somebody
 * wrote it, and a second edit would have recorded the override as the original and lost the real
 * one for good.
 *
 * Cloned at module load, which is the only moment nothing has merged anything yet.
 */
const SHIPPED: Record<SupportedLocale, unknown> = {
  da: structuredClone(da),
  en: structuredClone(en),
}

export const i18n = createI18n({
  // The Composition API mode. `legacy: false` is what makes `useI18n()` work in `<script setup>`.
  legacy: false,
  locale: DEFAULT_LOCALE,
  fallbackLocale: DEFAULT_LOCALE,
  messages: { da, en },

  // A missing key renders as the key itself rather than as nothing. Ugly on screen, but a blank
  // label is a bug somebody ships; a key on screen is a bug somebody reports.
  missingWarn: import.meta.env.DEV,
  fallbackWarn: import.meta.env.DEV,
})

export const t = i18n.global.t

/**
 * The shipped catalogue for one language, flattened to dotted keys.
 *
 * Read from the snapshot rather than from the running catalogue, because the running one has the
 * workspace's overrides merged into it — and the screen that lets somebody write an override has
 * to show what they are overriding, not what they already wrote.
 */
export function baseMessages(locale: SupportedLocale): Record<string, string> {
  const flat: Record<string, string> = {}

  const walk = (node: Record<string, unknown>, prefix: string) => {
    for (const [key, value] of Object.entries(node)) {
      const path = prefix ? `${prefix}.${key}` : key

      if (typeof value === 'string') flat[path] = value
      else if (value && typeof value === 'object') walk(value as Record<string, unknown>, path)
    }
  }

  walk(SHIPPED[locale] as Record<string, unknown>, '')

  return flat
}

/**
 * Picks the language, most specific first: what this person chose, then what the workspace reads,
 * then the browser, then Danish.
 *
 * The browser is consulted before the default so that an English-speaking visitor to a public
 * sign-up page is not shown Danish just because the shop never set anything — but after the
 * workspace, because a shop that has chosen its language has chosen it for its customers too.
 */
export function resolveLocale(
  userLocale?: string | null,
  tenantLocale?: string | null,
): SupportedLocale {
  if (isSupported(userLocale)) return userLocale
  if (isSupported(tenantLocale)) return tenantLocale

  const browser = navigator.language?.split('-')[0]
  if (isSupported(browser)) return browser

  return DEFAULT_LOCALE
}

/**
 * Applies a language, and pulls in the workspace's own wording for it.
 *
 * Overrides are merged rather than replacing the catalogue, so a workspace that has reworded four
 * phrases still gets every other string — and the next release's new keys arrive translated
 * without anybody touching the overrides.
 */
export async function applyLocale(locale: SupportedLocale): Promise<void> {
  i18n.global.locale.value = locale
  document.documentElement.lang = locale

  try {
    const response = await fetch(`/api/i18n/${locale}`, { credentials: 'include' })
    if (!response.ok) return

    const overrides = (await response.json()) as Record<string, string>
    if (!overrides || Object.keys(overrides).length === 0) return

    // The keys are dotted paths and the catalogue is nested, so they are expanded before merging.
    // vue-i18n resolves `a.b.c` through the object, not through a flat map.
    i18n.global.mergeLocaleMessage(locale, expand(overrides))
  } catch {
    // A workspace's wording is a refinement, not a requirement. If it cannot be fetched the
    // application still speaks — in the shipped language — which is a far better failure than a
    // screen that will not load.
  }
}

function expand(flat: Record<string, string>): Record<string, unknown> {
  const nested: Record<string, unknown> = {}

  for (const [path, value] of Object.entries(flat)) {
    const parts = path.split('.')
    let node = nested

    for (const part of parts.slice(0, -1)) {
      if (typeof node[part] !== 'object' || node[part] === null) node[part] = {}
      node = node[part] as Record<string, unknown>
    }

    node[parts[parts.length - 1]] = value
  }

  return nested
}
